import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { COPY } from "../shared/copy";
import type { OptionKey } from "../shared/types";
import { cleanProse } from "../shared/sanitize";

// Candidate review. Shows the EXACT future game material. The subject approves,
// reframes (same source fact), edits, or rejects. Nothing is gameable until PLAY IT.
export function Candidates() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState<any[]>([]);
  const [approvedCount, setApprovedCount] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<any>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // The screen used to render "Nothing left to review" and "You've got 0
  // questions ready" for a beat before the real rows arrived, which reads as
  // "the AI produced nothing" at exactly the wrong moment.
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");

  async function load() {
    if (!user) return;
    // Scoped to this league. Without it someone in two leagues sees both leagues'
    // pending questions in one undifferentiated list with no way to tell which is
    // which, and approves things for a league they were not thinking about.
    const { data: rows } = await supabase
      .from("question_candidates")
      .select("*")
      .eq("owner_user_id", user.id)
      .eq("league_id", leagueId)
      .order("created_at", { ascending: true });
    setPending((rows ?? []).filter((r) => r.status === "pending"));
    setApprovedCount((rows ?? []).filter((r) => r.status === "approved").length);
    setLoading(false);
  }
  useEffect(() => { load(); }, [user, leagueId]);

  // Every action here used to swallow its outcome. A reframe the model could not
  // produce came back 200 with {status:"reframe_failed"}, which was discarded,
  // so the button reloaded the identical card and looked broken. A network
  // failure rejected an un-awaited promise and did nothing at all.
  async function act(candidateId: string, op: string, extra: any = {}) {
    setBusy(candidateId);
    setNote("");
    try {
      const r = await api<any>(`/api/candidates/action`, { body: { op, candidateId, ...extra } });
      if (r?.status === "reframe_failed") {
        setNote("Couldn't come up with a different version of that one. Try Edit to word it yourself, or Don't use it.");
      } else if (op === "approve") {
        setNote("Locked in. That one can show up in a drop now.");
      } else if (op === "reject") {
        setNote("Gone. Nobody sees that one, ever.");
      } else if (op === "reframe") {
        setNote("Here's another angle on the same thing.");
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setNote(e?.message ?? "That didn't go through. Try it again.");
    } finally {
      setBusy(null);
    }
  }

  const letters: OptionKey[] = ["a", "b", "c", "d"];

  return (
    <div className="app">
      <div className="header">
        <h1>{COPY.candidates.intro}</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Done</button>
      </div>
      <p className="muted">{COPY.candidates.readyCount(approvedCount)}</p>

      {note && (
        <div className="banner">
          <span>{note}</span>
          <button className="link" onClick={() => setNote("")}>dismiss</button>
        </div>
      )}

      {loading && <div className="loading">Loading your questions…</div>}

      {!loading && pending.length === 0 && (
        <div className="card live">
          <span className="tag">{approvedCount > 0 ? "You're set" : "Nothing to review"}</span>
          {approvedCount > 0 ? (
            <>
              <p>
                You&apos;ve got <b>{approvedCount}</b> question{approvedCount === 1 ? "" : "s"} ready about you.
                They&apos;ll show up in a drop when the time comes, and you sit out your own.
              </p>
              <button className="btn primary" onClick={() => navigate(`/league/${leagueId}`)}>Back to league</button>
              <button className="link" onClick={() => navigate(`/league/${leagueId}/checkin`)}>Add another update</button>
            </>
          ) : (
            <>
              <p>Nothing is waiting on you right now. Give the league an update and we&apos;ll build questions from it.</p>
              <button className="btn primary" onClick={() => navigate(`/league/${leagueId}/checkin`)}>Check in</button>
              <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Back to league</button>
            </>
          )}
        </div>
      )}

      {pending.map((c) =>
        editing === c.id ? (
          <EditCard
            key={c.id}
            candidate={draft}
            onChange={setDraft}
            onCancel={() => setEditing(null)}
            onSave={() => act(c.id, "edit", { question: draft })}
          />
        ) : (
          <div className="card" key={c.id}>
            <span className="tag">{COPY.candidates.labelQuestion}</span>
            <p><b>{cleanProse(c.question_text)}</b></p>
            <span className="tag">{COPY.candidates.labelOptions}</span>
            <div className="grid">
              {letters.map((l) => (
                <div key={l} className={`answer ${c.correct_option === l ? "correct" : ""}`}>
                  {l.toUpperCase()}. {c[`option_${l}`]}
                </div>
              ))}
            </div>
            <span className="tag">{COPY.candidates.labelCorrect}</span>
            <p>{c.correct_option.toUpperCase()}. {c[`option_${c.correct_option}`]}</p>
            <span className="tag">{COPY.candidates.labelReveal}</span>
            <p className="muted">{cleanProse(c.reveal_context)}</p>
            <div className="grid">
              <button className="btn primary" disabled={busy === c.id} onClick={() => act(c.id, "approve")}>
                {busy === c.id ? "Working…" : COPY.candidates.playIt}
              </button>
              <div className="row">
                <button className="btn ghost" disabled={busy === c.id} onClick={() => act(c.id, "reframe")}>{COPY.candidates.reframe}</button>
                <button className="btn ghost" disabled={busy === c.id} onClick={() => { setDraft({ ...c }); setEditing(c.id); }}>{COPY.candidates.edit}</button>
              </div>
              <button className="btn danger" disabled={busy === c.id} onClick={() => act(c.id, "reject")}>{COPY.candidates.dontUse}</button>
            </div>
          </div>
        )
      )}
    </div>
  );
}

function EditCard({ candidate, onChange, onCancel, onSave }: { candidate: any; onChange: (c: any) => void; onCancel: () => void; onSave: () => void }) {
  const letters: OptionKey[] = ["a", "b", "c", "d"];
  const set = (k: string, v: string) => onChange({ ...candidate, [k]: v });
  return (
    <div className="card">
      <span className="tag">Edit question</span>
      <textarea className="input" value={candidate.question_text} onChange={(e) => set("question_text", e.target.value)} />
      {letters.map((l) => (
        <div className="row" key={l}>
          <input className="input" value={candidate[`option_${l}`]} onChange={(e) => set(`option_${l}`, e.target.value)} />
          <label className="pill">
            <input type="radio" name="correct" checked={candidate.correct_option === l} onChange={() => set("correct_option", l)} /> correct
          </label>
        </div>
      ))}
      <span className="tag">{COPY.candidates.labelReveal}</span>
      <textarea className="input" value={candidate.reveal_context} onChange={(e) => set("reveal_context", e.target.value)} />
      <div className="row">
        <button className="btn ghost" onClick={onCancel}>Cancel</button>
        <button className="btn primary" onClick={onSave}>Save & review</button>
      </div>
    </div>
  );
}
